import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import JsSIP from "jssip";
import { useAuth } from "@/contexts/AuthContext";
import { useSip } from "@/contexts/SipContext";
import { 
  isInternal, 
  isCallQueue, 
  getDisplayContacts, 
  getContactCounts 
} from "@/utils/contactFilters";
import { 
  Phone, 
  PhoneOff,
  PhoneIncoming,
  PhoneOutgoing,
  Delete, 
  User,
  UserCircle,
  Globe,
  Search,
  Plus,
  Mic,
  MicOff,
  Pause,
  Play,
  Wifi,
  WifiOff,
  RefreshCw,
  Star,
  ExternalLink,
  ArrowRightLeft,
  PhoneForwarded,
  Users,
  Clock,
  PhoneIncoming as PhoneIn,
  PhoneMissed,
  Building2,
  History,
  Hash
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { StarCodesPanel } from "@/components/StarCodesPanel";
import AddContactModal from "@/components/AddContactModal";

import { API_URL } from '../utils/api';
const API = API_URL;

const dialPadKeys = [
  { label: "1", sub: "" },
  { label: "2", sub: "ABC" },
  { label: "3", sub: "DEF" },
  { label: "4", sub: "GHI" },
  { label: "5", sub: "JKL" },
  { label: "6", sub: "MNO" },
  { label: "7", sub: "PQRS" },
  { label: "8", sub: "TUV" },
  { label: "9", sub: "WXYZ" },
  { label: "*", sub: "" },
  { label: "0", sub: "+" },
  { label: "#", sub: "" },
];

function DialPadButton({ label, sub, onClick, disabled }) {
  return (
    <button
      onClick={() => onClick(label)}
      disabled={disabled}
      className="dial-btn w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-secondary/50 hover:bg-secondary border border-border/50 hover:border-primary/30 flex flex-col items-center justify-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={`dial-btn-${label}`}
    >
      <span className="text-xl sm:text-2xl font-heading font-bold">{label}</span>
      {sub && <span className="text-[9px] sm:text-[10px] text-muted-foreground tracking-widest">{sub}</span>}
    </button>
  );
}

// Call status display component
function CallStatus({ status, duration, remoteName }) {
  const getStatusColor = () => {
    switch (status) {
      case "ringing": return "text-amber-500";
      case "connected": return "text-emerald-500";
      case "incoming": return "text-blue-500";
      default: return "text-muted-foreground";
    }
  };

  const getStatusText = () => {
    switch (status) {
      case "ringing": return "Ringing...";
      case "connected": return formatDuration(duration);
      case "incoming": return "Incoming Call";
      case "ending": return "Ending...";
      default: return "Ready";
    }
  };

  const formatDuration = (s) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="text-center">
      <p className={`text-sm font-medium ${getStatusColor()}`}>{getStatusText()}</p>
      {remoteName && status !== "idle" && (
        <p className="text-xs text-muted-foreground mt-1">{remoteName}</p>
      )}
    </div>
  );
}

export default function Dialer() {
  // Get authenticated user
  const { user, token } = useAuth();
  
  // URL params for number pre-fill
  const [searchParams] = useSearchParams();
  
  // Load default contact filter from localStorage (default to "internal")
  const getDefaultContactFilter = () => {
    return localStorage.getItem('defaultContactFilter') || 'internal';
  };
  
  // Get SIP context for global call management
  const sip = useSip();
  
  // UI State
  const [phoneNumber, setPhoneNumber] = useState("");
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [newContact, setNewContact] = useState({ name: "", phone_number: "", company: "", email: "", notes: "", source: "personal" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [contactTab, setContactTab] = useState(getDefaultContactFilter);
  const [panelTab, setPanelTab] = useState("contacts"); // "contacts" or "starcodes"

  // Use SIP context state (fallback to local state for backward compatibility during transition)
  const sipStatus = sip.sipStatus;
  const callStatus = sip.callStatus;
  const callDuration = sip.callDuration;
  const isMuted = sip.isMuted;
  const isOnHold = sip.isOnHold;
  const incomingCallInfo = sip.incomingCallInfo;
  
  // Keep refs from context for transfer functionality
  const userAgentRef = sip.userAgentRef;
  const sessionRef = sip.sessionRef;

  // Transfer State
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferSearch, setTransferSearch] = useState("");
  const [transferTarget, setTransferTarget] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);

  // Caller Info Panel State (shown during incoming/active calls)
  const [callerDetails, setCallerDetails] = useState(sip.callerDetails);
  const [loadingCallerInfo, setLoadingCallerInfo] = useState(false);
  
  // Use caller history from SIP context
  const callerHistory = sip.callerHistory || [];

  // Track previous call status to detect call completion
  const prevCallStatusRef = useRef(callStatus);

  // Local refs for audio and consultation calls (transfer feature)
  const remoteAudioRef = useRef(null);
  const consultationAudioRef = useRef(null);
  const consultationSessionRef = useRef(null);

  // State for TURN server configuration
  const [turnConfig, setTurnConfig] = useState(null);
  
  // Clear phone number when call ends (transitions from non-idle to idle)
  useEffect(() => {
    if (prevCallStatusRef.current !== 'idle' && callStatus === 'idle') {
      // Call just ended - clear the dialer
      setPhoneNumber('');
    }
    prevCallStatusRef.current = callStatus;
  }, [callStatus]);

  // Sync caller details from context
  useEffect(() => {
    if (sip.callerDetails) {
      setCallerDetails(sip.callerDetails);
    }
  }, [sip.callerDetails]);

  // Fetch caller details and history when there's an incoming call
  // Now delegates to SIP context which handles both contact lookup and history
  const fetchCallerInfo = async (callerNumber) => {
    if (!callerNumber) return;
    
    setLoadingCallerInfo(true);
    try {
      // Use the SIP context's fetchCallerInfo which fetches both contact and history
      await sip.fetchCallerInfo(callerNumber);
    } catch (error) {
      console.error("Failed to fetch caller info:", error);
    } finally {
      setLoadingCallerInfo(false);
    }
  };

  // Clear caller info when call ends
  const clearCallerInfo = () => {
    setCallerDetails(null);
  };

  // Fetch TURN server credentials on mount
  useEffect(() => {
    const fetchTurnConfig = async () => {
      try {
        const res = await axios.get(`${API}/turn/credentials`);
        if (res.data.configured) {
          setTurnConfig(res.data);
          console.log("TURN server configured:", res.data.server_url);
        }
      } catch (e) {
        console.log("No TURN config available");
      }
    };
    fetchTurnConfig();
  }, []);

  // Get audio constraints with preferred devices from localStorage
  // Includes echo cancellation, noise suppression, and auto gain control
  const getAudioConstraints = () => {
    const preferredMic = localStorage.getItem('preferredMicrophone');
    
    // Audio processing options to prevent echo/feedback
    const audioProcessing = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    
    if (preferredMic && preferredMic !== 'default') {
      return {
        audio: {
          deviceId: { exact: preferredMic },
          ...audioProcessing
        },
        video: false
      };
    }
    
    return {
      audio: audioProcessing,
      video: false
    };
  };

  // Set output device on audio element
  const setAudioOutputDevice = async (audioElement) => {
    const preferredSpeaker = localStorage.getItem('preferredSpeaker');
    
    if (preferredSpeaker && audioElement && typeof audioElement.setSinkId === 'function') {
      try {
        await audioElement.setSinkId(preferredSpeaker);
        console.log('Audio output set to:', preferredSpeaker);
      } catch (error) {
        console.warn('Could not set audio output device:', error);
        // Device ID might have changed - try to find by label
        const savedLabel = localStorage.getItem('preferredSpeakerLabel');
        if (savedLabel) {
          try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const matchingDevice = devices.find(d => d.kind === 'audiooutput' && d.label === savedLabel);
            if (matchingDevice) {
              await audioElement.setSinkId(matchingDevice.deviceId);
              console.log('Audio output set by label match:', savedLabel, '->', matchingDevice.deviceId);
              localStorage.setItem('preferredSpeaker', matchingDevice.deviceId);
            }
          } catch (labelError) {
            console.warn('Could not find device by label:', labelError);
          }
        }
      }
    }
  };

  // Build ICE servers configuration including TURN if configured
  // TURN server uses SIP credentials for authentication
  const getIceServers = () => {
    const servers = [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
      { urls: "stun:stun2.l.google.com:19302" },
      { urls: "stun:stun3.l.google.com:19302" },
      { urls: "stun:stun4.l.google.com:19302" },
    ];
    
    // Add TURN server if configured - uses SIP credentials for authentication
    if (turnConfig?.server_url && user?.phone_number && user?.sip_password) {
      servers.push({
        urls: turnConfig.server_url,
        username: user.phone_number,  // Use SIP phone number
        credential: user.sip_password  // Use SIP password
      });
      console.log("Using TURN server:", turnConfig.server_url, "with SIP credentials for user:", user.phone_number);
    }
    
    return servers;
  };

  // Extract SIP domain from user's sip_domain (wss://wss.securevoip.nz:7443 -> wss.securevoip.nz)
  const getSipDomain = () => {
    if (!user?.sip_domain) return "wss.securevoip.nz";
    try {
      const url = new URL(user.sip_domain);
      return url.hostname;
    } catch {
      return user.sip_domain.replace("wss://", "").replace(":7443", "");
    }
  };

  // Handle URL number parameter
  useEffect(() => {
    const numberParam = searchParams.get("number");
    if (numberParam) {
      setPhoneNumber(numberParam);
    }
  }, [searchParams]);

  // State for busy extensions (on a call)
  const [busyExtensions, setBusyExtensions] = useState(new Set());
  
  // State for online extensions (softphone active)
  const [onlineExtensions, setOnlineExtensions] = useState(new Set());
  
  // State for user profile images (keyed by phone number)
  const [userImages, setUserImages] = useState({});

  // Fetch contacts on mount
  useEffect(() => {
    fetchContacts();
    fetchBusyExtensions();
    fetchOnlineExtensions();
    fetchUserImages();
    
    // Poll busy extensions every 5 seconds
    const busyInterval = setInterval(fetchBusyExtensions, 5000);
    // Poll online extensions every 10 seconds
    const onlineInterval = setInterval(fetchOnlineExtensions, 10000);
    
    return () => {
      // Cleanup on unmount
      clearInterval(busyInterval);
      clearInterval(onlineInterval);
    };
  }, []);

  // Report presence status when on the Dialer page
  useEffect(() => {
    if (!token) return;
    
    // Report idle status immediately when page loads
    reportSoftphoneStatus('idle');
    
    // Send heartbeat every 60 seconds to stay "online"
    const heartbeatInterval = setInterval(() => {
      // Only report idle if not currently on a call
      if (callStatus === 'idle') {
        reportSoftphoneStatus('idle');
      }
    }, 60000);
    
    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [token]);

  // Report call status changes for presence
  useEffect(() => {
    if (!token) return;
    
    // Map call status to presence status
    if (callStatus === 'connected' || callStatus === 'on-call') {
      reportSoftphoneStatus('on-call');
    } else if (callStatus === 'ringing' || callStatus === 'incoming') {
      reportSoftphoneStatus('ringing');
    } else if (callStatus === 'idle') {
      reportSoftphoneStatus('idle');
    }
  }, [callStatus, token]);

  // SIP connection is now handled globally by SipContext
  // No need for auto-connect here - it happens on app load

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(res.data);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    }
  };

  const fetchBusyExtensions = async () => {
    try {
      const res = await axios.get(`${API}/webhooks/active-calls`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusyExtensions(new Set(res.data.busy_extensions || []));
    } catch (error) {
      console.error("Failed to fetch busy extensions:", error);
    }
  };
  
  const fetchOnlineExtensions = async () => {
    try {
      const res = await axios.get(`${API}/softphone/online`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnlineExtensions(new Set(res.data.online_extensions || []));
    } catch (error) {
      console.error("Failed to fetch online extensions:", error);
    }
  };
  
  const fetchUserImages = async () => {
    try {
      const res = await axios.get(`${API}/users/images/by-phone`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserImages(res.data || {});
    } catch (error) {
      console.error("Failed to fetch user images:", error);
    }
  };

  // Report softphone status to backend for presence tracking
  const reportSoftphoneStatus = async (status, callId = null, remoteParty = null) => {
    if (!token) return;
    try {
      await axios.post(`${API}/softphone/status`, {
        status,
        call_id: callId,
        remote_party: remoteParty
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Failed to report softphone status:", error);
    }
  };

  // Check if a contact is currently on a call
  const isOnCall = (contact) => {
    return busyExtensions.has(contact.phone_number) || 
           (contact.extension && busyExtensions.has(contact.extension));
  };

  // Check if a contact is online (softphone active)
  const isOnline = (contact) => {
    return onlineExtensions.has(contact.phone_number) || 
           (contact.extension && onlineExtensions.has(contact.extension));
  };

  // Setup audio element for remote audio
  const setupRemoteAudio = useCallback(async (session) => {
    const remoteStream = new MediaStream();
    
    session.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = remoteStream;
      
      // Set preferred speaker output device
      await setAudioOutputDevice(remoteAudioRef.current);
      
      remoteAudioRef.current.play().catch(e => console.log("Audio play error:", e));
    }
  }, []);

  // Local setupRemoteAudio for consultation calls during transfer
  const setupConsultationAudio = useCallback(async (session) => {
    const remoteStream = new MediaStream();
    session.sessionDescriptionHandler?.peerConnection?.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });

    if (!consultationAudioRef.current) {
      consultationAudioRef.current = new Audio();
    }
    consultationAudioRef.current.srcObject = remoteStream;
    
    const preferredSpeaker = localStorage.getItem('preferredSpeaker');
    if (preferredSpeaker && typeof consultationAudioRef.current.setSinkId === 'function') {
      try {
        await consultationAudioRef.current.setSinkId(preferredSpeaker);
      } catch (error) {
        console.warn('Could not set consultation audio output device:', error);
      }
    }

    consultationAudioRef.current.play().catch(e => console.log("Consultation audio play error:", e));
  }, []);

  // Connect to SIP server - now handled by SipContext globally
  // These functions are kept for manual reconnection if needed
  const connectSIP = async () => {
    await sip.initializeSip();
  };

  // Disconnect from SIP server
  const disconnectSIP = async () => {
    await sip.disconnectSip();
  };

  // Make outgoing call - use context function
  const makeCall = async (numberToCall = null) => {
    // Handle case where React event object is passed instead of a number
    const dialNumber = (typeof numberToCall === 'string' && numberToCall) 
      ? numberToCall.trim() 
      : phoneNumber.trim();
      
    if (!dialNumber) {
      toast.error("Please enter a phone number");
      return;
    }

    const success = await sip.makeCall(dialNumber);
    if (success) {
      toast.info(`Calling ${dialNumber}...`);
      fetchCallerInfo(dialNumber);
    }
  };

  // Answer incoming call - use context function
  const answerCall = async () => {
    await sip.answerCall();
    // Fetch additional caller info (includes call history)
    if (incomingCallInfo?.number) {
      fetchCallerInfo(incomingCallInfo.number);
    }
  };

  // Reject incoming call - use context function
  const rejectCall = () => {
    sip.rejectCall();
    clearCallerInfo();
  };

  // Hang up call - use context function
  const hangupCall = () => {
    sip.hangUp();
    clearCallerInfo();
  };

  // Blind Transfer - immediately transfers the call using JsSIP
  const blindTransfer = async (targetNumber) => {
    if (!sessionRef.current || callStatus !== "connected") {
      toast.error("No active call to transfer");
      return;
    }

    setIsTransferring(true);
    
    try {
      // Use SIP context's blind transfer which handles JsSIP refer
      const success = sip.blindTransfer(targetNumber);
      
      if (success) {
        setTransferDialogOpen(false);
        setTransferSearch("");
        setTransferTarget("");
      }
    } catch (error) {
      console.error("Blind transfer error:", error);
      toast.error(`Transfer failed: ${error.message}`);
    } finally {
      setIsTransferring(false);
    }
  };

  // Attended Transfer - calls target first, then transfers original caller
  // Using JsSIP for consultation call
  const attendedTransfer = async (targetNumber) => {
    if (!sessionRef.current || callStatus !== "connected") {
      toast.error("No active call to transfer");
      return;
    }

    setIsTransferring(true);
    const sipDomain = sip.getSipDomain();
    const originalSession = sessionRef.current;
    
    // Store the target number for later use in transfer completion
    const transferTargetNumber = targetNumber;
    const targetUri = `sip:${targetNumber}@${sipDomain}`;
    
    try {
      // Step 1: Put original call on FULL hold - mute both incoming AND outgoing audio
      // This ensures the original caller cannot hear the consultation conversation
      let originalReceiverTracks = [];
      let originalSenderInfo = []; // Store sender and original track for restoration
      let silentStreamForHold = null;
      
      try {
        // JsSIP uses .connection instead of .sessionDescriptionHandler?.peerConnection
        const originalPC = originalSession.connection;
        if (originalPC) {
          // Mute incoming audio from original caller (so receptionist doesn't hear them)
          originalPC.getReceivers().forEach(receiver => {
            if (receiver.track && receiver.track.kind === 'audio') {
              originalReceiverTracks.push(receiver.track);
              receiver.track.enabled = false;
            }
          });
          
          // For outgoing audio: Replace with silent track instead of disabling
          // This prevents the original caller from hearing us, while keeping the mic available for consultation call
          const audioContext = new AudioContext();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          gainNode.gain.value = 0; // Silent
          oscillator.connect(gainNode);
          const destination = audioContext.createMediaStreamDestination();
          gainNode.connect(destination);
          oscillator.start();
          silentStreamForHold = destination.stream;
          const silentTrack = silentStreamForHold.getAudioTracks()[0];
          
          // Replace each sender's track with the silent track
          for (const sender of originalPC.getSenders()) {
            if (sender.track && sender.track.kind === 'audio') {
              const originalTrack = sender.track;
              originalSenderInfo.push({ sender, originalTrack });
              await sender.replaceTrack(silentTrack);
              console.log("Replaced sender track with silent track for hold");
            }
          }
          
          console.log("Original call placed on hold - muted", originalReceiverTracks.length, "receiver tracks, replaced", originalSenderInfo.length, "sender tracks with silence");
        }
      } catch (e) {
        console.warn("Could not put original call on hold:", e);
      }

      toast.info(`Calling ${targetNumber}...`);

      // Step 2: Create consultation call using JsSIP via the UserAgent from context
      const ua = sip.userAgentRef.current;
      if (!ua) {
        throw new Error("SIP UserAgent not available");
      }

      const preferredMic = localStorage.getItem('preferredMicrophone');
      const audioConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
      
      if (preferredMic) {
        audioConstraints.deviceId = { exact: preferredMic };
      }

      const callOptions = {
        mediaConstraints: {
          audio: audioConstraints,
          video: false
        },
        pcConfig: {
          iceServers: sip.getIceServers()
        },
        rtcOfferConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false
        }
      };

      const consultSession = ua.call(targetUri, callOptions);
      
      if (!consultSession) {
        throw new Error("Failed to initiate consultation call");
      }

      // Store reference to consultation session
      consultationSessionRef.current = consultSession;

      // Function to restore original call audio (both directions)
      const restoreOriginalAudio = async () => {
        // Restore incoming audio from original caller
        originalReceiverTracks.forEach(track => {
          track.enabled = true;
        });
        
        // Restore original microphone tracks to senders (replace silent track back with real mic)
        for (const { sender, originalTrack } of originalSenderInfo) {
          try {
            await sender.replaceTrack(originalTrack);
            console.log("Restored original microphone track to sender");
          } catch (e) {
            console.warn("Could not restore sender track:", e);
          }
        }
        
        // Clean up the silent audio context
        if (silentStreamForHold) {
          silentStreamForHold.getTracks().forEach(track => track.stop());
        }
        
        console.log("Original call restored - unmuted", originalReceiverTracks.length, "receiver tracks and restored", originalSenderInfo.length, "sender tracks");
        
        // Reconnect original call audio to speaker
        try {
          const originalPC = originalSession.connection;
          if (originalPC && remoteAudioRef.current) {
            originalPC.getReceivers().forEach(receiver => {
              if (receiver.track && receiver.track.kind === 'audio') {
                const stream = new MediaStream([receiver.track]);
                remoteAudioRef.current.srcObject = stream;
                remoteAudioRef.current.play().catch(() => {});
              }
            });
          }
        } catch (e) {
          console.warn("Could not restore original audio:", e);
        }
        
        // Also clear the consultation audio
        if (consultationAudioRef.current) {
          consultationAudioRef.current.srcObject = null;
        }
      };

      // Handle consultation call events (JsSIP style)
      consultSession.on('confirmed', async () => {
        console.log("Consultation call confirmed");
        
        // Route consultation call audio to SEPARATE audio element
        try {
          const consultPC = consultSession.connection;
          
          if (consultPC) {
            // Create a MediaStream for consultation audio
            const remoteStream = new MediaStream();
            
            // Use ontrack event - this fires when media actually arrives
            consultPC.ontrack = async (event) => {
              console.log("Consultation ontrack event fired:", event.track.kind);
              if (event.track.kind === 'audio') {
                if (!remoteStream.getAudioTracks().includes(event.track)) {
                  remoteStream.addTrack(event.track);
                }
                
                if (consultationAudioRef.current) {
                  consultationAudioRef.current.srcObject = remoteStream;
                  consultationAudioRef.current.volume = 1.0;
                  consultationAudioRef.current.muted = false;
                  
                  await setAudioOutputDevice(consultationAudioRef.current);
                  
                  consultationAudioRef.current.play()
                    .then(() => console.log("Consultation audio playing"))
                    .catch(e => console.error("Consultation audio play error:", e));
                }
              }
            };
            
            // Also try getting tracks from receivers (with delay)
            setTimeout(async () => {
              const receivers = consultPC.getReceivers();
              
              receivers.forEach((receiver) => {
                if (receiver.track && receiver.track.kind === 'audio') {
                  if (!remoteStream.getAudioTracks().includes(receiver.track)) {
                    remoteStream.addTrack(receiver.track);
                  }
                }
              });
              
              if (consultationAudioRef.current && remoteStream.getAudioTracks().length > 0) {
                if (consultationAudioRef.current.srcObject !== remoteStream) {
                  consultationAudioRef.current.srcObject = remoteStream;
                }
                consultationAudioRef.current.volume = 1.0;
                consultationAudioRef.current.muted = false;
                
                await setAudioOutputDevice(consultationAudioRef.current);
                
                try {
                  await consultationAudioRef.current.play();
                } catch (e) {
                  console.error("Delayed consultation audio play error:", e);
                }
              }
            }, 500);
          }
        } catch (e) {
          console.error("Consultation audio setup error:", e);
        }
        
        toast.success(`Connected to ${targetNumber}`, { duration: 5000 });
        setTransferDialogOpen(false);
        setTransferSearch("");
        setTransferTarget("");
      });

      consultSession.on('ended', async () => {
        console.log("Consultation call ended");
        
        if (consultationAudioRef.current) {
          consultationAudioRef.current.srcObject = null;
        }
        
        // Restore original call audio if still active
        if (originalSession && !originalSession.isEnded()) {
          await restoreOriginalAudio();
          toast.info("Back to original call");
        }
        
        consultationSessionRef.current = null;
        setIsTransferring(false);
      });

      consultSession.on('failed', async (e) => {
        console.log("Consultation call failed:", e.cause);
        toast.error(`Consultation call failed: ${e.cause || 'Unknown error'}`);
        
        await restoreOriginalAudio();
        consultationSessionRef.current = null;
        setIsTransferring(false);
      });

      // Store functions for transfer completion/cancellation
      window._completeAttendedTransfer = async () => {
        try {
          toast.info("Completing transfer...");
          
          // End the consultation call first
          if (consultationSessionRef.current && !consultationSessionRef.current.isEnded()) {
            try {
              consultationSessionRef.current.terminate();
            } catch (e) {
              console.debug("Consultation terminate error:", e);
            }
          }
          
          // Now do a blind transfer of the original call to the target using JsSIP refer
          if (originalSession && !originalSession.isEnded()) {
            try {
              originalSession.refer(targetUri);
              toast.success(`Transfer completed to ${transferTargetNumber}`);
              
              // End the receptionist's leg of the call after successful transfer
              setTimeout(() => {
                try {
                  if (originalSession && !originalSession.isEnded()) {
                    originalSession.terminate();
                    console.log("Receptionist call ended after successful transfer");
                  }
                } catch (e) {
                  console.debug("Post-transfer terminate error:", e);
                }
                sip.hangUp();
              }, 500);
              
              setIsTransferring(false);
              consultationSessionRef.current = null;
            } catch (err) {
              toast.error(`Transfer failed: ${err.message}`);
              restoreOriginalAudio();
              setIsTransferring(false);
            }
          } else {
            toast.error("Cannot complete transfer - original call ended");
            setIsTransferring(false);
          }
        } catch (err) {
          console.error("Transfer completion error:", err);
          toast.error(`Transfer failed: ${err.message}`);
          await restoreOriginalAudio();
          setIsTransferring(false);
        }
      };

      window._cancelAttendedTransfer = async () => {
        // End consultation call
        if (consultationSessionRef.current && !consultationSessionRef.current.isEnded()) {
          try {
            consultationSessionRef.current.terminate();
          } catch (e) {
            console.debug("Cancel terminate error:", e);
          }
        }
        
        // Restore original call
        await restoreOriginalAudio();
        toast.info("Transfer cancelled");
        setIsTransferring(false);
      };
      
    } catch (error) {
      console.error("Attended transfer error:", error);
      toast.error(`Attended transfer failed: ${error.message}`);
      setIsTransferring(false);
    }
  };
  
  // Get internal contacts for transfer list (using centralized filter)
  const getInternalContactsForTransfer = () => {
    return getDisplayContacts(contacts).filter(c => isInternal(c));
  };

  // Filter contacts for transfer search
  const getFilteredTransferContacts = () => {
    const internal = getInternalContactsForTransfer();
    if (!transferSearch) return internal;
    
    const search = transferSearch.toLowerCase();
    return internal.filter(c => 
      c.name?.toLowerCase().includes(search) ||
      c.phone_number?.includes(search) ||
      c.extension?.includes(search)
    );
  };

  // Toggle mute - use context function
  const toggleMute = () => {
    sip.toggleMute();
  };

  // Toggle hold - use context function
  const toggleHold = () => {
    sip.toggleHold();
  };

  // Send DTMF
  const sendDTMF = (digit) => {
    sip.sendDtmf(digit);
    setPhoneNumber(prev => prev + digit);
  };

  const handleDialPadClick = (key) => {
    if (callStatus === "connected") {
      sendDTMF(key);
    } else {
      setPhoneNumber(prev => prev + key);
    }
  };

  const handleBackspace = () => {
    setPhoneNumber(prev => prev.slice(0, -1));
  };

  const handleContactClick = (contact) => {
    // One-click calling - directly initiate call when clicking a contact on the dialer
    if (sipStatus === 'registered' && callStatus === 'idle') {
      setPhoneNumber(contact.phone_number);
      // Pass number directly to avoid state timing issues
      makeCall(contact.phone_number);
    } else {
      // If not ready to call, just fill in the number
      setPhoneNumber(contact.phone_number);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.name || !newContact.phone_number) {
      toast.error("Name and phone number are required");
      return;
    }
    try {
      const contactData = {
        name: newContact.name.trim(),
        phone_number: newContact.phone_number,
        source: newContact.source || "personal"
      };
      
      // Add optional fields if provided
      if (newContact.company?.trim()) contactData.company = newContact.company.trim();
      if (newContact.email?.trim()) contactData.email = newContact.email.trim();
      if (newContact.notes?.trim()) contactData.notes = newContact.notes.trim();
      
      await axios.post(`${API}/contacts`, contactData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const typeLabel = newContact.source === "company" ? "company" : "personal";
      toast.success(`Contact added as ${typeLabel} contact`);
      setNewContact({ name: "", phone_number: "", company: "", email: "", notes: "", source: "personal" });
      setDialogOpen(false);
      fetchContacts();
    } catch (error) {
      toast.error("Failed to add contact");
    }
  };

  // Use centralized contact filters - get display-ready contacts (no test, no queues)
  const displayContacts = getDisplayContacts(contacts);
  const { total: allCount, internal: internalCount, external: externalCount } = getContactCounts(contacts);
  const personalCount = displayContacts.filter(c => c.source === 'personal').length;

  const filteredContacts = displayContacts.filter(c => {
    // First filter by search
    const query = searchQuery.toLowerCase();
    const matchesSearch = c.name.toLowerCase().includes(query) ||
      c.phone_number.includes(searchQuery) ||
      (c.company && c.company.toLowerCase().includes(query)) ||
      (c.extension && c.extension.includes(searchQuery));
    
    if (!matchesSearch) return false;
    
    // Then filter by tab
    if (contactTab === "internal") return isInternal(c);
    if (contactTab === "external") return !isInternal(c);
    if (contactTab === "personal") return c.source === 'personal';
    return true;
  });

  const isInCall = ["ringing", "connected", "incoming"].includes(callStatus);
  const canMakeCall = sipStatus === "registered" && phoneNumber && callStatus === "idle";

  // Status indicator color
  const getStatusColor = () => {
    switch (sipStatus) {
      case "registered": return "bg-emerald-500";
      case "connecting": return "bg-amber-500 animate-pulse";
      case "failed": return "bg-red-500";
      default: return "bg-zinc-500";
    }
  };

  const getStatusText = () => {
    switch (sipStatus) {
      case "registered": return "Online";
      case "connecting": return "Connecting...";
      case "failed": return "Failed";
      default: return "Offline";
    }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="dialer-page">
      {/* Hidden audio elements for remote audio */}
      <audio ref={remoteAudioRef} autoPlay />
      <audio ref={consultationAudioRef} autoPlay />
      
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Softphone</h1>
          <p className="text-muted-foreground text-sm">Make and receive calls in your browser</p>
        </div>
        
        {/* Connection Status */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/50 border border-border/50">
            <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
            <span className="text-sm font-medium">{getStatusText()}</span>
          </div>
          
          {sipStatus === "disconnected" || sipStatus === "failed" ? (
            <Button onClick={connectSIP} size="sm" className="gap-2" data-testid="connect-btn">
              <Wifi className="w-4 h-4" />
              Connect
            </Button>
          ) : sipStatus === "connecting" ? (
            <Button disabled size="sm" className="gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Connecting
            </Button>
          ) : (
            <Button onClick={disconnectSIP} variant="outline" size="sm" className="gap-2" data-testid="disconnect-btn">
              <WifiOff className="w-4 h-4" />
              Disconnect
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Dialer */}
        <div className="lg:col-span-2">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4 sm:p-6">
              {/* Attended Transfer Controls */}
              {isTransferring && callStatus === "connected" && (
                <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                  <div className="text-center mb-3">
                    <p className="font-medium text-blue-500">Attended Transfer in Progress</p>
                    <p className="text-sm text-muted-foreground">Speaking with transfer target...</p>
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button 
                      onClick={() => window._completeAttendedTransfer?.()}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="complete-transfer-btn"
                    >
                      <PhoneForwarded className="w-4 h-4 mr-2" />
                      Complete Transfer
                    </Button>
                    <Button 
                      onClick={() => window._cancelAttendedTransfer?.()}
                      variant="outline"
                      data-testid="cancel-transfer-btn"
                    >
                      <PhoneOff className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Incoming Call Alert */}
              {callStatus === "incoming" && incomingCallInfo && (
                <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <PhoneIncoming className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-medium text-blue-500">Incoming Call</p>
                        <p className="text-sm text-muted-foreground">{incomingCallInfo.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{incomingCallInfo.number}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={rejectCall} variant="destructive" size="sm" className="gap-1" data-testid="reject-call-btn">
                        <PhoneOff className="w-4 h-4" />
                        Decline
                      </Button>
                      <Button onClick={answerCall} size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="answer-call-btn">
                        <Phone className="w-4 h-4" />
                        Answer
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Phone Number Display */}
              <div className="mb-6">
                <div className={`rounded-xl p-3 sm:p-4 border transition-colors ${
                  isInCall 
                    ? callStatus === "connected" 
                      ? "bg-emerald-500/10 border-emerald-500/30" 
                      : "bg-amber-500/10 border-amber-500/30"
                    : "bg-secondary/30 border-border/50"
                }`}>
                  <Input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder={isInCall ? "" : "Enter number"}
                    className="text-center text-xl sm:text-2xl font-mono bg-transparent border-none focus-visible:ring-0 h-auto py-1"
                    data-testid="phone-input"
                    disabled={isInCall && callStatus !== "connected"}
                  />
                  
                  {/* Call Status */}
                  <div className="mt-1">
                    <CallStatus 
                      status={callStatus} 
                      duration={callDuration}
                      remoteName={incomingCallInfo?.name}
                    />
                  </div>
                </div>
              </div>

              {/* In-Call Controls */}
              {callStatus === "connected" && (
                <div className="flex flex-col items-center gap-4 mb-6">
                  {/* Transfer Button - Large and prominent (hidden when transfer in progress) */}
                  {!isTransferring && (
                    <Button 
                      onClick={() => setTransferDialogOpen(true)}
                      className="w-full max-w-xs h-14 text-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="transfer-btn"
                    >
                      <ArrowRightLeft className="w-6 h-6 mr-3" />
                      TRANSFER
                    </Button>
                  )}
                  
                  {/* Mute and Hold controls */}
                  <div className="flex justify-center gap-3">
                    <Button 
                      variant={isMuted ? "destructive" : "outline"} 
                      size="lg" 
                      onClick={toggleMute}
                      className="h-12 px-5 rounded-full flex items-center gap-2"
                      data-testid="mute-btn"
                    >
                      {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                      <span className="font-medium">{isMuted ? "UNMUTE" : "MUTE"}</span>
                    </Button>
                    <Button 
                      variant={isOnHold ? "destructive" : "outline"} 
                      size="lg" 
                      onClick={toggleHold}
                      className="h-12 px-5 rounded-full flex items-center gap-2"
                      data-testid="hold-btn"
                    >
                      {isOnHold ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                      <span className="font-medium">{isOnHold ? "RESUME" : "HOLD"}</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* Dial Pad Grid */}
              <div className="flex justify-center mb-6">
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {dialPadKeys.map(key => (
                    <DialPadButton 
                      key={key.label} 
                      label={key.label} 
                      sub={key.sub} 
                      onClick={handleDialPadClick}
                      disabled={sipStatus !== "registered" && callStatus !== "connected"}
                    />
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-4">
                <Button 
                  variant="outline" 
                  size="lg" 
                  onClick={handleBackspace} 
                  className="w-16 sm:w-20 h-14" 
                  data-testid="backspace-btn"
                  disabled={isInCall}
                >
                  <Delete className="w-5 h-5" />
                </Button>
                
                {isInCall ? (
                  <Button 
                    size="lg" 
                    onClick={hangupCall}
                    className="w-32 sm:w-40 h-14 bg-red-600 hover:bg-red-700" 
                    data-testid="hangup-btn"
                  >
                    <PhoneOff className="w-5 h-5 mr-2" />
                    End Call
                  </Button>
                ) : (
                  <Button 
                    size="lg" 
                    onClick={() => makeCall()} 
                    disabled={!canMakeCall} 
                    className="w-32 sm:w-40 h-14 btn-glow bg-emerald-600 hover:bg-emerald-700" 
                    data-testid="call-btn"
                  >
                    <PhoneOutgoing className="w-5 h-5 mr-2" />
                    Call
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Contacts OR Caller Info Panel */}
        <div className="flex flex-col h-[calc(100vh-12rem)]">
          {/* Show Caller Info Panel during incoming/active calls, otherwise show contacts */}
          {(callStatus === "incoming" || callStatus === "connected" || callStatus === "ringing") && (callerDetails || incomingCallInfo) ? (
            /* Caller Info Panel */
            <Card className="bg-card border-border/50 flex flex-col flex-1 overflow-hidden">
              <CardHeader className="pb-4 flex-shrink-0 border-b border-border/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    {callStatus === "incoming" ? (
                      <>
                        <PhoneIn className="w-5 h-5 text-emerald-500 animate-pulse" />
                        Incoming Call
                      </>
                    ) : (
                      <>
                        <Phone className="w-5 h-5 text-primary" />
                        {callStatus === "ringing" ? "Calling..." : "On Call"}
                      </>
                    )}
                  </CardTitle>
                  {callStatus === "connected" && (
                    <Badge variant="outline" className="font-mono">
                      <Clock className="w-3 h-3 mr-1" />
                      {Math.floor(callDuration / 60).toString().padStart(2, '0')}:{(callDuration % 60).toString().padStart(2, '0')}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 overflow-y-auto p-0">
                {/* Caller Details */}
                <div className="p-6 border-b border-border/50 bg-secondary/20">
                  {loadingCallerInfo ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                        {callerDetails?.isUnknown ? (
                          <Phone className="w-8 h-8 text-primary" />
                        ) : (
                          <User className="w-8 h-8 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-semibold truncate">
                          {callerDetails?.name || incomingCallInfo?.name || "Unknown Caller"}
                        </h3>
                        <p className="text-muted-foreground font-mono">
                          {callerDetails?.phone_number || incomingCallInfo?.number}
                        </p>
                        {callerDetails?.company && (
                          <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                            <Building2 className="w-3 h-3" />
                            {callerDetails.company}
                          </div>
                        )}
                        {callerDetails?.extension && (
                          <Badge variant="secondary" className="mt-2 font-mono">
                            Ext. {callerDetails.extension}
                          </Badge>
                        )}
                        {callerDetails?.isUnknown && (
                          <Badge variant="outline" className="mt-2">
                            Unknown Contact
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Call History with this caller */}
                <div className="p-4">
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <History className="w-4 h-4 text-muted-foreground" />
                    Call History
                  </h4>
                  
                  {callerHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No previous calls with this contact
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {callerHistory.map((call, index) => {
                        // Support both CDR format and local call log format
                        const direction = (call.direction || '').toLowerCase();
                        const isInbound = direction === 'inbound';
                        const status = (call.status || '').toLowerCase();
                        const isMissed = status === 'no answer' || status === 'missed' || status === 'busy' || call.disposition === 'NO ANSWER';
                        const duration = call.seconds || call.duration || call.billsec || 0;
                        const dateStr = call.date && call.time 
                          ? `${call.date} ${call.time}` 
                          : call.start_time || call.created_at;
                        
                        return (
                          <div 
                            key={call.id || index}
                            className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border/30"
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              isInbound
                                ? 'bg-emerald-500/20 text-emerald-500'
                                : isMissed
                                  ? 'bg-red-500/20 text-red-500'
                                  : 'bg-blue-500/20 text-blue-500'
                            }`}>
                              {isMissed ? (
                                <PhoneMissed className="w-4 h-4" />
                              ) : isInbound ? (
                                <PhoneIn className="w-4 h-4" />
                              ) : (
                                <PhoneOutgoing className="w-4 h-4" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">
                                {isInbound ? 'Incoming' : 'Outgoing'}
                                {isMissed && ' (Missed)'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {dateStr ? new Date(dateStr).toLocaleString() : 'Unknown date'}
                              </p>
                            </div>
                            {duration > 0 && (
                              <Badge variant="outline" className="text-xs font-mono">
                                {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Contacts & Star Codes Panel (shown when not in a call) */
            <Card className="bg-card border-border/50 flex flex-col flex-1 overflow-hidden">
              <CardHeader className="pb-2 flex-shrink-0">
                {/* Panel Tabs - Contacts vs Star Codes */}
                <Tabs value={panelTab} onValueChange={setPanelTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 h-9">
                    <TabsTrigger value="contacts" className="text-sm gap-2" data-testid="panel-tab-contacts">
                      <Users className="w-4 h-4" />
                      Contacts
                    </TabsTrigger>
                    <TabsTrigger value="starcodes" className="text-sm gap-2" data-testid="panel-tab-starcodes">
                      <Hash className="w-4 h-4" />
                      Star Codes
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardHeader>
              
              {panelTab === "contacts" ? (
                <>
                  {/* Contacts Header */}
                  <div className="px-6 pb-2 flex-shrink-0">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-muted-foreground">Quick dial your contacts</span>
                      <div className="flex items-center gap-1">
                        <Link to="/contacts">
                          <Button variant="ghost" size="sm" data-testid="view-all-contacts-btn">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid="add-contact-btn">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                        </Dialog>
                        <AddContactModal
                          open={dialogOpen}
                          onOpenChange={setDialogOpen}
                          onSaved={() => fetchContacts()}
                        />
                      </div>
                    </div>
                    
                    {/* Contact Tabs */}
                    <Tabs value={contactTab} onValueChange={setContactTab}>
                      <TabsList className="grid w-full grid-cols-4 h-8">
                        <TabsTrigger value="all" className="text-xs px-1" data-testid="softphone-tab-all">
                          All ({allCount})
                        </TabsTrigger>
                        <TabsTrigger value="internal" className="text-xs px-1" data-testid="softphone-tab-internal">
                          <UserCircle className="w-3 h-3 mr-1" />
                          ({internalCount})
                        </TabsTrigger>
                        <TabsTrigger value="external" className="text-xs px-1" data-testid="softphone-tab-external">
                          <Globe className="w-3 h-3 mr-1" />
                          ({externalCount})
                        </TabsTrigger>
                        <TabsTrigger value="personal" className="text-xs px-1" data-testid="softphone-tab-personal">
                          <User className="w-3 h-3 mr-1" />
                          ({personalCount})
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    
                    <div className="relative mt-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        placeholder="Search contacts..." 
                        value={searchQuery} 
                        onChange={e => setSearchQuery(e.target.value)} 
                        className="pl-10 bg-secondary/50" 
                        data-testid="contact-search-input" 
                      />
                    </div>
                  </div>
                  
                  <CardContent className="space-y-2 flex-1 overflow-y-auto pt-2">
                    {filteredContacts.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground text-sm">
                          {contacts.length === 0 
                            ? "No contacts yet" 
                            : contactTab === "internal" 
                              ? "No internal contacts" 
                              : contactTab === "external"
                                ? "No external contacts"
                                : contactTab === "personal"
                                  ? "No personal contacts"
                                  : "No matches found"}
                        </p>
                        <Link to="/contacts">
                          <Button variant="link" size="sm" className="mt-2">
                            Manage Contacts
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <>
                        {filteredContacts.map(contact => (
                          <button
                            key={contact.id}
                            onClick={() => handleContactClick(contact)}
                            disabled={isInCall}
                            className="w-full flex items-center gap-3 p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 border border-border/30 hover:border-primary/30 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid={`contact-${contact.id}`}
                          >
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center relative overflow-hidden">
                              {(userImages[contact.phone_number] || contact.profile_image) ? (
                                <img 
                                  src={userImages[contact.phone_number] || contact.profile_image} 
                                  alt={contact.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <User className="w-4 h-4 text-primary" />
                              )}
                              {contact.favorite && (
                                <Star className="w-3 h-3 text-amber-500 fill-amber-500 absolute -top-1 -right-1" />
                              )}
                              {/* Presence indicator for internal contacts */}
                              {isInternal(contact) && (
                                <span 
                                  className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${
                                    isOnCall(contact) 
                                      ? 'bg-red-500' 
                                      : isOnline(contact) 
                                        ? 'bg-emerald-500' 
                                        : 'bg-zinc-400'
                                  }`}
                                  title={isOnCall(contact) ? 'On a call' : isOnline(contact) ? 'Logged In' : 'Offline'}
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium truncate">{contact.name}</p>
                                {isInternal(contact) && (
                                  <>
                                    <span 
                                      className={`w-2 h-2 rounded-full ${
                                        isOnCall(contact) 
                                          ? 'bg-red-500' 
                                          : isOnline(contact) 
                                            ? 'bg-emerald-500' 
                                            : 'bg-zinc-400'
                                      }`}
                                      title={isOnCall(contact) ? 'On a call' : isOnline(contact) ? 'Logged In' : 'Offline'}
                                    />
                                    {isOnCall(contact) && (
                                      <Badge variant="destructive" className="text-[10px] px-1 py-0">
                                        Busy
                                      </Badge>
                                    )}
                                  </>
                                )}
                                {contact.source && contact.source !== 'local' && !isInternal(contact) && (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                                    {contact.source}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <p className="text-xs text-muted-foreground font-mono">{contact.phone_number}</p>
                                {contact.extension && (
                                  <Badge variant="secondary" className="text-[10px] font-mono px-1 py-0">
                                    x{contact.extension}
                                  </Badge>
                                )}
                              </div>
                              {contact.company && <p className="text-xs text-muted-foreground truncate">{contact.company}</p>}
                            </div>
                          </button>
                        ))}
                        <Link to="/contacts" className="block mt-3">
                          <Button variant="ghost" size="sm" className="w-full text-xs">
                            View All Contacts
                          </Button>
                        </Link>
                      </>
                    )}
                  </CardContent>
                </>
              ) : (
                /* Star Codes Panel */
                <StarCodesPanel 
                  onDial={(code) => {
                    setPhoneNumber(code);
                    sip.makeCall(code);
                  }} 
                />
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-500" />
              Transfer Call
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search Contacts */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Search internal contacts</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or extension..."
                  value={transferSearch}
                  onChange={(e) => {
                    setTransferSearch(e.target.value);
                    // Clear selection when typing to search
                    if (transferTarget && !e.target.value.includes(transferSearch)) {
                      setTransferTarget("");
                    }
                  }}
                  className="pl-10"
                  data-testid="transfer-search-input"
                />
              </div>
            </div>

            {/* Internal Contacts List */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-1">
                <UserCircle className="w-4 h-4" />
                Internal Contacts {transferTarget && <span className="text-blue-500">• Selected</span>}
              </Label>
              <div className="max-h-60 overflow-y-auto space-y-1 border rounded-lg p-2">
                {getFilteredTransferContacts().length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {transferSearch ? "No matching contacts" : "No internal contacts"}
                  </p>
                ) : (
                  getFilteredTransferContacts().map(contact => (
                    <button
                      key={contact.id}
                      onClick={() => {
                        const target = contact.extension || contact.phone_number;
                        setTransferTarget(target);
                        setTransferSearch(contact.name);
                      }}
                      disabled={isOnCall(contact)}
                      className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                        isOnCall(contact) 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-secondary/50'
                      } ${transferTarget === (contact.extension || contact.phone_number) ? 'bg-blue-500/20 border border-blue-500/50' : ''}`}
                      data-testid={`transfer-contact-${contact.phone_number}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                        {(userImages[contact.phone_number] || contact.profile_image) ? (
                          <img 
                            src={userImages[contact.phone_number] || contact.profile_image} 
                            alt={contact.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm truncate">{contact.name}</p>
                          {isOnCall(contact) && (
                            <Badge variant="destructive" className="text-[10px] px-1 py-0">
                              Busy
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground font-mono">
                          {contact.extension ? `x${contact.extension}` : contact.phone_number}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Transfer Buttons - always visible, disabled until contact selected */}
            <div className="flex gap-2">
              <Button 
                onClick={() => {
                  if (transferTarget) {
                    blindTransfer(transferTarget);
                  }
                }}
                disabled={!transferTarget || isTransferring}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                data-testid="blind-transfer-btn"
              >
                <PhoneForwarded className="w-4 h-4 mr-2" />
                {isTransferring ? "Transferring..." : "Blind Transfer"}
              </Button>
              <Button 
                onClick={() => {
                  if (transferTarget) {
                    attendedTransfer(transferTarget);
                  }
                }}
                disabled={!transferTarget || isTransferring}
                variant="outline"
                className="flex-1 disabled:opacity-50"
                data-testid="attended-transfer-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Attended
              </Button>
            </div>

            {/* Help text */}
            <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
              <p><strong>Blind Transfer:</strong> Immediately transfers the call</p>
              <p><strong>Attended Transfer:</strong> Speak to recipient first, then connect them</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
